import { apiFetch } from './api';

// ─── TYPES ──────────────────────────────────────────────────────────────────

export interface MaintenanceRecord {
  id: string;
  homeId: string;
  assetId?: string | null;
  title: string;
  description?: string | null;
  /** pending | upcoming | completed | skipped | snoozed */
  status: string;
  performedAt?: string | null;
  nextDueAt?: string | null;
  cost?: number | null;
  createdAt: string;
  asset?: { id: string; name: string } | null;
}

export interface CreateMaintenanceDto {
  homeId: string;
  assetId?: string;
  title: string;
  description?: string;
  performedAt?: string;
  nextDueAt?: string;
  cost?: number;
  status?: string;
}

/** Auto-generated scheduled maintenance task */
export interface ScheduledTask {
  id: string;
  homeId: string;
  assetId: string;
  assetName: string;
  assetLocation?: string | null;
  assetBrand?: string | null;
  title: string;
  description?: string | null;
  whyItMatters?: string | null;
  category: string;
  frequency: string;
  intervalDays: number;
  estimatedMinutes?: number | null;
  estimatedCost?: string | null;
  difficultyLevel?: string | null;
  effectiveFrom: string;
  dueDate: string;
  lastCompletedAt?: string | null;
  /** upstream derived: upcoming | overdue | completed | skipped | snoozed */
  status: 'upcoming' | 'overdue' | 'completed' | 'skipped' | 'snoozed';
  /** upstream derived priority based on category, difficulty, and diyAvailable */
  priority?: 'high' | 'medium' | 'low';
  snoozedUntil?: string | null;
  skipReason?: string | null;
  diyAvailable: boolean;
  comingSoon: boolean;
  /**
   * AI-generated DIY steps stored at task creation time.
   * Reuse these instead of calling OpenAI again when opening the DIY drawer.
   */
  diyStepsJson?: Array<{
    title: string;
    description: string;
    instructions?: string[];
    safetyNote?: string;
    toolsNeeded?: string[];
    duration?: string;
    riskLevel?: string;
  }>;
  /**
   * AI-generated parts list stored at task creation time.
   * Reuse these instead of calling OpenAI again when opening the Order Parts drawer.
   */
  partsJson?: Array<{
    name: string;
    qty: number;
    description: string;
    price?: number;
  }>;
  aiGenerated?: boolean;
  aiGeneratedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateScheduledTaskDto {
  status: 'completed' | 'skipped' | 'snoozed';
  snoozedUntil?: string;
  skipReason?: 'already_done' | 'not_applicable' | 'other_method';
  performedAt?: string;
}

export interface DiyStepsResponse {
  steps: Array<{
    stepNumber: number;
    title: string;
    description: string;
    toolsNeeded?: string[];
    safetyNote?: string;
    estimatedMinutes?: number;
  }>;
  totalDuration: string;
  difficultyLevel: string;
  riskLevel: string;
  toolsRequired: string[];
  safetyWarnings: string[];
}

export interface MaintenancePartsResponse {
  parts: Array<{
    id: string;
    name: string;
    description: string;
    quantity: number;
    estimatedPrice: string;
    partNumber?: string;
    whereToFind?: string;
  }>;
  totalEstimatedCost: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

// ─── Legacy Record APIs ──────────────────────────────────────────────────────

export const getMaintenanceRecords = async (homeId: string): Promise<MaintenanceRecord[]> => {
  const res = await apiFetch<ApiResponse<MaintenanceRecord[]>>(
    `/v1/maintenance?homeId=${encodeURIComponent(homeId)}`
  );
  return res.data;
};

export const createMaintenanceRecord = async (
  data: CreateMaintenanceDto
): Promise<MaintenanceRecord> => {
  const res = await apiFetch<ApiResponse<MaintenanceRecord>>('/v1/maintenance', {
    method: 'POST',
    body: data,
  });
  return res.data;
};

export const getMaintenanceRecordById = async (id: string): Promise<MaintenanceRecord> => {
  const res = await apiFetch<ApiResponse<MaintenanceRecord>>(`/v1/maintenance/${id}`);
  return res.data;
};

export const updateMaintenanceRecord = async (
  id: string,
  data: Partial<Pick<MaintenanceRecord, 'status' | 'performedAt' | 'nextDueAt' | 'cost'>>
): Promise<MaintenanceRecord> => {
  const res = await apiFetch<ApiResponse<MaintenanceRecord>>(`/v1/maintenance/${id}`, {
    method: 'PUT',
    body: data,
  });
  return res.data;
};

// ─── Smart Schedule APIs ─────────────────────────────────────────────────────

export const getMaintenanceSchedule = async (
  homeId: string,
  options?: { assetId?: string; status?: string }
): Promise<ScheduledTask[]> => {
  const params = new URLSearchParams({ homeId });
  if (options?.assetId) params.set('assetId', options.assetId);
  if (options?.status) params.set('status', options.status);
  const res = await apiFetch<ApiResponse<ScheduledTask[]>>(
    `/v1/maintenance/schedule?${params.toString()}`
  );
  return res.data;
};

export const updateScheduledTask = async (
  taskId: string,
  data: UpdateScheduledTaskDto
): Promise<ScheduledTask> => {
  const res = await apiFetch<ApiResponse<ScheduledTask>>(
    `/v1/maintenance/schedule/${taskId}`,
    { method: 'PATCH', body: data }
  );
  return res.data;
};

export const generateAssetSchedule = async (
  assetId: string,
  conditionOnAdd?: string
): Promise<ScheduledTask[]> => {
  const res = await apiFetch<ApiResponse<ScheduledTask[]>>(
    `/v1/assets/${assetId}/generate-schedule`,
    { method: 'POST', body: { conditionOnAdd } }
  );
  return res.data;
};

export const getAssetSchedule = async (assetId: string): Promise<ScheduledTask[]> => {
  const res = await apiFetch<ApiResponse<ScheduledTask[]>>(`/v1/assets/${assetId}/schedule`);
  return res.data;
};

// ─── AI APIs ─────────────────────────────────────────────────────────────────

export const generateDiySteps = async (params: {
  taskName: string;
  assetName: string;
  assetLocation?: string;
  whyItMatters?: string;
  estimatedEffort?: string;
  taskId?: string;
}): Promise<DiyStepsResponse> => {
  const res = await apiFetch<ApiResponse<DiyStepsResponse>>('/v1/ai/generate-diy-steps', {
    method: 'POST',
    body: params,
  });
  return res.data;
};

export const generateMaintenanceParts = async (params: {
  taskName: string;
  assetName: string;
  assetId?: string;
  taskDescription?: string;
  taskId?: string;
}): Promise<MaintenancePartsResponse> => {
  const res = await apiFetch<ApiResponse<MaintenancePartsResponse>>(
    '/v1/ai/generate-maintenance-parts',
    { method: 'POST', body: params }
  );
  return res.data;
};

